import { useState, useRef, useCallback, RefObject } from 'react'


export interface StatefulRef<T> {
  readonly current: T | null
}


/**
 * React's useRef returns a mutable reference to the ref element.
 * This means that you can't use it as a dependency in functions like
 * useEffect.
 *
 * This hook acts the same as `useRef`, except it will trigger a state
 * update when the ref has been assigned.
 */


/* Originally copied from: https://github.com/Bedrock-Layouts/Bedrock/blob/90e564191d9e661d60af1f2d99e3b68fa6330c78/packages/use-stateful-ref/src/index.tsx 
 * Modified to to return a RefObject with a read-only current & separate setter function
*/
export function useStatefulRef<T extends HTMLElement = HTMLElement>(
  initialVal: T | null = null,
): [React.RefObject<T | null>, (value: T | null) => void] {
  const ref = useRef<T | null>(initialVal)
  const [, setCur] = useState(0)

  const setCurrent = useCallback((value: T | null) => {
    if (ref.current !== value) {
      ref.current = value
      setCur(cur => cur + 1)
    }
  }, [])

  return [ref, setCurrent]
}
